import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/use-auth';
import { cacheService } from "./cacheService";

// Definir interface para as variáveis de ambiente do Vite
interface ImportMetaEnv {
  VITE_N8N_SHARED_URL: string;
  VITE_N8N_SHARED_KEY: string;
  [key: string]: any;
}

interface ImportMeta {
  env: ImportMetaEnv;
}

interface N8nConfig {
  apiUrl: string;
  apiKey: string;
  webhookUrl: string;
  workspace_id?: string;  
  workflow_id?: string;   // ID do workflow padrão do usuário
}

// Cache durations
const CONFIG_CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
const CONNECTION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const WORKFLOWS_CACHE_DURATION = 2 * 60 * 1000; // 2 minutos

// Função para gerar um ID único
const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export class N8nProxyService {
  private isConnectionTestInProgress: boolean = false;
  private lastConnectionTest: number = 0;
  private connectionTestInterval: number = 5 * 60 * 1000; // 5 minutos
  private backgroundTasks: Map<string, Promise<any>> = new Map();
  
  // Configurações da instância compartilhada
  private sharedN8nConfig = {
    apiUrl: import.meta.env.VITE_N8N_SHARED_URL,
    apiKey: import.meta.env.VITE_N8N_SHARED_KEY
  };
  
  /**
   * Obter configurações do n8n específicas do usuário
   */
  async getN8nConfig(): Promise<N8nConfig> {
    try {
      console.log('[n8nProxyService] Iniciando getN8nConfig...');
      
      // Tenta obter do cache primeiro
      const cachedConfig = cacheService.get<N8nConfig>('n8n:config');
      if (cachedConfig) {
        console.log('[n8nProxyService] Usando configuração em cache');
        return cachedConfig;
      }
      
      const user = await supabase.auth.getUser();
      const userId = user.data.user?.id;
      
      if (!userId) {
        console.log('[n8nProxyService] Usuário não autenticado');
        throw new Error('Usuário não autenticado');
      }
      
      console.log(`[n8nProxyService] Buscando configuração para usuário: ${userId}`);
      
      // Buscar apenas o workspace_id do usuário no banco de dados
      const { data, error } = await supabase
        .from('integration_config')
        .select('config')
        .eq('type', 'n8n')
        .eq('user_id', userId)
        .single();
      
      // Configuração padrão usando a instância compartilhada
      const sharedConfig: N8nConfig = {
        ...this.sharedN8nConfig,
        webhookUrl: '',
        workspace_id: undefined,
        workflow_id: undefined
      };
      
      // Se encontramos dados do usuário, adicionamos o workspace_id e workflow_id
      if (data?.config) {
        sharedConfig.workspace_id = data.config.workspace_id;
        sharedConfig.workflow_id = data.config.workflow_id;
        console.log('[n8nProxyService] Workspace encontrado:', data.config.workspace_id);
      } else {
        console.log('[n8nProxyService] Nenhum workspace encontrado para o usuário');
      }
      
      // Armazena no cache para futuras consultas
      cacheService.set('n8n:config', sharedConfig, CONFIG_CACHE_DURATION);
      
      return sharedConfig;
    } catch (error) {
      console.error('[n8nProxyService] Erro ao buscar configurações:', error);
      // Em caso de erro, retornamos a configuração compartilhada sem workspace
      return { 
        ...this.sharedN8nConfig,
        webhookUrl: '', 
        workspace_id: undefined, 
        workflow_id: undefined 
      };
    }
  }
  
  /**
   * Salvar configurações do n8n específicas do usuário
   */
  async saveN8nConfig(config: N8nConfig): Promise<void> {
    try {
      console.log('[n8nProxyService] Iniciando salvamento de configurações n8n');
      
      // Obter o ID do usuário primeiro
      const userResponse = await supabase.auth.getUser();
      const userId = userResponse.data.user?.id;
      
      if (!userId) {
        console.error('[n8nProxyService] Erro: Usuário não autenticado');
        throw new Error('Usuário não autenticado');
      }
      
      console.log(`[n8nProxyService] Usuário autenticado: ${userId}`);
      
      // Verificar se a configuração já existe para este usuário
      const { data: existingConfig, error: queryError } = await supabase
        .from('integration_config')
        .select('*')
        .eq('type', 'n8n')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (queryError) {
        console.error('[n8nProxyService] Erro ao consultar configuração existente:', queryError);
      }
      
      // Log de configuração existente ou não
      console.log('[n8nProxyService] Configuração existente:', existingConfig ? 'Sim' : 'Não');
      
      // Preparar o objeto de configuração
      const configData = {
        type: 'n8n',
        config: config,
        user_id: userId,
        updated_at: new Date().toISOString()
      };
      
      console.log('[n8nProxyService] Dados a serem salvos:', {
        ...configData,
        config: {
          apiUrl: config.apiUrl,
          apiKeyProvided: !!config.apiKey,
          webhookUrlProvided: !!config.webhookUrl
        }
      });
      
      let result;
      
      // Se já existe, atualizar
      if (existingConfig) {
        console.log('[n8nProxyService] Atualizando configuração existente');
        result = await supabase
          .from('integration_config')
          .update(configData)
          .eq('type', 'n8n')
          .eq('user_id', userId);
      } else {
        // Se não existe, inserir
        console.log('[n8nProxyService] Inserindo nova configuração');
        result = await supabase
          .from('integration_config')
          .insert(configData);
      }
      
      if (result.error) {
        console.error('[n8nProxyService] Erro na operação:', result.error);
        throw result.error;
      }
      
      console.log('[n8nProxyService] Configuração salva com sucesso');
    } catch (error) {
      console.error('[n8nProxyService] Erro ao salvar configurações:', error);
      throw new Error(`Erro ao salvar configurações do n8n: ${error.message || 'Erro desconhecido'}`);
    }
  }
  
  /**
   * Faz uma chamada à API do n8n através da Edge Function n8n-proxy
   * @param endpoint Endpoint da API do n8n (ex: 'workflows', 'webhooks')
   * @param options Opções da requisição
   * @returns Dados da resposta da API
   */
  async callN8nApi<T = any>(
    endpoint: string,
    options: {
      method?: string;
      body?: any;
      queryParams?: Record<string, string>;
      useCache?: boolean;
      cacheTime?: number;
      workflowId?: string; // Adicionar opção para especificar o ID do workflow
      retryOnFailure?: boolean; // Nova opção para controlar tentativas após falha
    } = {}
  ): Promise<T> {
    const { 
      method = 'GET', 
      body, 
      queryParams = {}, 
      useCache = true,
      cacheTime = WORKFLOWS_CACHE_DURATION,
      workflowId,
      retryOnFailure = true // Por padrão, vai tentar novamente se falhar
    } = options;
    
    // Verificar cache para requisições GET
    if (useCache && method === 'GET') {
      const cacheKey = `n8n:${endpoint}:${JSON.stringify(queryParams || {})}:${workflowId || ''}`;
      const cachedData = cacheService.get<T>(cacheKey);
      if (cachedData) {
        console.log(`[n8nProxyService] Usando dados em cache para: ${endpoint}`);
        
        // Inicie uma atualização em segundo plano para manter o cache fresco
        this.refreshCacheInBackground<T>(endpoint, options, cacheKey, cacheTime);
        
        return cachedData;
      }
    }
    
    // Verificação específica para endpoints de workflow
    const isWorkflowsEndpoint = endpoint === 'workflows' || endpoint.includes('/workflows');
    const isConnectionValidation = endpoint === 'workflows' && method === 'GET';
    
    // Obter a mais recente configuração de API URL e chave
    const apiUrl = import.meta.env.VITE_N8N_SHARED_URL;
    const apiKey = import.meta.env.VITE_N8N_SHARED_KEY;
    
    // Construir a URL base corretamente
    const baseUrl = apiUrl.endsWith('/') 
               ? apiUrl.slice(0, -1) 
               : apiUrl;
    
    // Etapa 1: Primeiro, fazer uma chamada geral para verificar conexão 
    if (isWorkflowsEndpoint && !workflowId) {
      // Se estamos tentando acessar workflows sem um ID específico, primeiro validar a conexão
      try {
        console.log('[n8nProxyService] Validando conexão com n8n...');
        // Chamada genérica apenas para verificar se a API está respondendo
        const validationUrl = `${baseUrl}/api/v1/workflows`;
        
        const { error: validationError } = await supabase.functions.invoke('n8n-proxy', {
          body: {
            url: validationUrl,
            method: 'GET',
            apiKey: apiKey
          }
        });
        
        if (validationError) {
          console.error('[n8nProxyService] Falha na validação de conexão:', validationError);
          throw new Error('Falha ao conectar com o serviço de automação');
        }
        
        console.log('[n8nProxyService] Conexão com n8n validada com sucesso');
      } catch (error) {
        console.error('[n8nProxyService] Erro ao validar conexão:', error);
        throw new Error('Não foi possível verificar a conexão com o serviço de automação');
      }
    }
    
    // Etapa 2: Buscar configuração do usuário para obter o workflow_id
    const user = await supabase.auth.getUser();
    if (!user.data.user) {
      throw new Error('Usuário não autenticado');
    }
    
    // Buscar configuração apenas do banco de dados, não usar cache para este passo
    const { data: configData, error: configError } = await supabase
      .from('integration_config')
      .select('config')
      .eq('type', 'n8n')
      .eq('user_id', user.data.user.id)
      .single();
    
    // Verificar se há erros na busca de configuração
    if (configError && !configError.message.includes('No rows found')) {
      console.error('[n8nProxyService] Erro ao buscar configuração do usuário:', configError);
      throw new Error('Erro ao buscar configuração do usuário');
    }
    
    // Etapa 3: Verificar se o usuário tem um workflow_id válido
    let userWorkflowId = null;
    
    // Se encontramos uma configuração e ela tem um workflow_id, use-o
    if (configData?.config && configData.config.workflow_id) {
      userWorkflowId = configData.config.workflow_id;
      console.log(`[n8nProxyService] Workflow ID do usuário encontrado: ${userWorkflowId}`);
    } else {
      // Etapa 3: Se não há workflow_id, o usuário precisará ativar a integração
      console.log('[n8nProxyService] Usuário não possui workflow configurado');
      
      // Se o endpoint espera um ID específico, mas o usuário não tem um, lançar erro para mostrar o botão
      if (endpoint.includes('/workflows/') && endpoint !== 'workflows') {
        throw new Error('WORKFLOW_NOT_PROVISIONED');
      }
      
      // Para endpoints genéricos, podemos apenas retornar uma lista vazia se apropriado
      if (endpoint === 'workflows' && method === 'GET') {
        console.log('[n8nProxyService] Retornando lista vazia de workflows');
        return [] as unknown as T;
      }
    }
    
    // Etapa 4: Usar apenas o workflow_id verificado do usuário ou o fornecido explicitamente
    // Prioridade: 1) ID fornecido explicitamente como parâmetro, 2) ID verificado do usuário
    const finalWorkflowId = workflowId || userWorkflowId;
    
    // Construir a URL completa
    let url = `${baseUrl}/api/v1/${endpoint}`;
    
    // Lógica de URL baseada no tipo de chamada
    if (isWorkflowsEndpoint && !isConnectionValidation) {
      if (finalWorkflowId) {
        // Se temos um ID de workflow específico, construir a URL para esse workflow
        if (!endpoint.includes(`/workflows/${finalWorkflowId}`)) {
          // Se o endpoint ainda não contém o ID específico
          if (endpoint === 'workflows' || endpoint === '/workflows') {
            // Construir URL específica para o workflow de acordo com o padrão da API n8n: /workflows/{id}
            url = `${baseUrl}/api/v1/workflows/${finalWorkflowId}`;
            console.log(`[n8nProxyService] Acessando workflow específico: ${finalWorkflowId}`);
          } else if (endpoint.startsWith('/workflows/') || endpoint.startsWith('workflows/')) {
            // Caso especial - já é uma chamada para um endpoint de workflow específico
            // Não alteramos o endpoint, mantemos o original
            console.log(`[n8nProxyService] Usando endpoint de workflow específico já formatado: ${endpoint}`);
          } else {
            // Para outros endpoints relacionados a workflows mas não específicos
            url = `${baseUrl}/api/v1/workflows/${finalWorkflowId}`;
            console.log(`[n8nProxyService] Convertendo endpoint para acessar workflow específico: ${finalWorkflowId}`);
          }
        }
      } else {
        // Se não temos um workflow específico, retornar lista vazia ou erro
        console.log('[n8nProxyService] Sem workflow definido para o usuário');
        if (endpoint === 'workflows') {
          return [] as unknown as T;
        } else {
          throw new Error('Workflow não configurado');
        }
      }
    }
    
    // Adicionar parâmetros de consulta se existirem
    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      url += `?${queryString}`;
    }
    
    console.log(`[n8nProxyService] Chamando API n8n via Edge Function: ${method} ${url}`);
    console.log(`[n8nProxyService] Workflow ID verificado: ${finalWorkflowId}`);
    
    try {
      // Chamar a Edge Function n8n-proxy
      const { data, error } = await supabase.functions.invoke('n8n-proxy', {
        body: {
          url,
          method,
          apiKey: apiKey, // Usar a chave API atualizada
          body: body ? JSON.stringify(body) : undefined,
          queryParams,
          workspace_id: configData?.config?.workspace_id, // Passar o workspace_id separadamente
          workflow_id: configData?.config?.workflow_id, // Adicionar workflow_id à chamada
          forceRefresh: !useCache || !retryOnFailure // Forçar o refresh na função se não estamos usando cache ou em retry
        }
      });
      
      if (error) {
        console.error(`[n8nProxyService] Erro na chamada à Edge Function:`, error);
        
        // Se for uma falha relacionada a credenciais e estamos permitindo retry
        if (retryOnFailure && 
            (error.message?.includes('unauthorized') || 
             error.message?.includes('authentication') ||
             error.message?.includes('credenciais'))) {
          
          console.log(`[n8nProxyService] Possível problema com credenciais em cache, tentando novamente com refresh...`);
          
          // Limpar o cache de configurações
          if (typeof cacheService.remove === 'function') {
            cacheService.remove('n8n:config');
          } else {
            // Alternativa: definir expirado
            cacheService.set('n8n:config', null, 0);
          }
          
          // Tentar novamente com refresh forçado, mas sem permitir mais retries (para evitar loop infinito)
          return this.callN8nApi(endpoint, {
            ...options,
            useCache: false,
            retryOnFailure: false
          });
        }
        
        throw new Error(`Erro na chamada à API n8n: ${error.message}`);
      }
      
      // Processar a resposta
      const responseData = data;
      
      // Armazenar em cache se for uma requisição GET
      if (useCache && method === 'GET') {
        const cacheKey = `n8n:${endpoint}:${JSON.stringify(queryParams)}:${finalWorkflowId || ''}`;
        cacheService.set(cacheKey, responseData, cacheTime);
      }
      
      return responseData;
    } catch (error) {
      console.error(`[n8nProxyService] Erro ao chamar Edge Function:`, error);
      throw new Error(`Erro ao chamar API n8n: ${error.message}`);
    }
  }

  /**
   * Testa a conexão com o n8n usando múltiplos métodos para garantir a conexão
   * @param forceRefresh Força um novo teste ignorando o cache
   * @param useBackgroundRefresh Se deve atualizar em segundo plano após retornar dados do cache
   */
  async testConnection(forceRefresh: boolean = false, useBackgroundRefresh: boolean = false): Promise<{ 
    connected: boolean; 
    mode?: string; 
    error?: string; 
    requestDetails?: any; 
    responseDetails?: any; 
    fromCache?: boolean; 
    isReady?: boolean;
  }> {
    console.log(`[n8nProxyService] Testando conexão com n8n (forceRefresh: ${forceRefresh}, useBackgroundRefresh: ${useBackgroundRefresh})`);
    
    // Verificar se há resultado em cache
    if (!forceRefresh) {
      const cachedStatus = cacheService.getConnectionStatus();
      if (cachedStatus) {
        console.log('[n8nProxyService] Usando status de conexão em cache:', cachedStatus);
        
        // Se solicitado, programa uma atualização em segundo plano
        if (useBackgroundRefresh) {
          this.scheduleBackgroundConnectionTest();
        }
        
        return {
          connected: cachedStatus.connected,
          mode: cachedStatus.mode,
          error: cachedStatus.error,
          fromCache: true,
          isReady: cachedStatus.isReady
        };
      }
    }
    
    // Se estamos já testando e não é forçado, retorne uma promise que resolverá quando o teste em andamento terminar
    if (this.isConnectionTestInProgress && !forceRefresh) {
      console.log('[n8nProxyService] Teste de conexão já em andamento, aguardando...');
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isConnectionTestInProgress) {
            clearInterval(checkInterval);
            const cachedStatus = cacheService.getConnectionStatus();
            if (cachedStatus) {
              resolve({
                connected: cachedStatus.connected,
                mode: cachedStatus.mode,
                error: cachedStatus.error,
                fromCache: true,
                isReady: cachedStatus.isReady
              });
            } else {
              // Se não houver cache, faça um novo teste
              this.testConnection(true).then(resolve);
            }
          }
        }, 1000);
      });
    }
    
    // Marcar que estamos testando a conexão
    this.isConnectionTestInProgress = true;
    this.lastConnectionTest = Date.now();
    
    try {
      console.log('[n8nProxyService] Iniciando teste de conexão com n8n');
      
      // Obter configurações do usuário
      const config = await this.getN8nConfig();
      
      // Construir a URL base corretamente
      const baseUrl = this.sharedN8nConfig.apiUrl.endsWith('/') 
                     ? this.sharedN8nConfig.apiUrl.slice(0, -1) 
                     : this.sharedN8nConfig.apiUrl;
      
      // Determinar endpoint para teste
      let url;
      let isReadyToUse = false;
      
      if (config.workflow_id) {
        // Se temos um workflow específico, verificar esse workflow em vez de buscar todos
        // Usar o formato correto para a API n8n: /workflows/{id}
        url = `${baseUrl}/api/v1/workflows/${config.workflow_id}`;
        isReadyToUse = true; // Se temos um workflow_id configurado, o sistema está pronto para uso
        console.log(`[n8nProxyService] Testando conexão com workflow específico: ${config.workflow_id}`);
      } else if (config.workspace_id) {
        // Se temos apenas workspace_id, verificar se há workflows para esse workspace
        url = `${baseUrl}/api/v1/workflows`;
        console.log(`[n8nProxyService] Testando conexão com workspace: ${config.workspace_id}`);
      } else {
        // Verificação simples da API sem filtros (apenas para validação inicial)
        url = `${baseUrl}/api/v1/workflows?limit=1`;
        console.log(`[n8nProxyService] Testando conexão básica sem workspace (limite 1)`);
      }
      
      // Chamar a Edge Function n8n-proxy para testar a conexão
      const { data, error } = await supabase.functions.invoke('n8n-proxy', {
        body: {
          url,
          method: 'GET',
          apiKey: this.sharedN8nConfig.apiKey,
          queryParams: config.workspace_id ? { workspace_id: config.workspace_id } : {},
          workspace_id: config.workspace_id, // Passar o workspace_id separadamente
          workflow_id: config.workflow_id // Adicionar workflow_id à chamada de teste
        }
      });
      
      // Verificar se a resposta foi bem-sucedida
      const connected = !error && data;
      const mode = config.workspace_id ? 'shared' : 'individual';
      
      // Determinar se o n8n está pronto para uso
      // Está pronto se temos um workflow_id ou se a conexão retornou dados
      if (connected && !isReadyToUse) {
        // Se estamos conectados mas não temos workflow_id definido, verificar se a resposta contém workflows
        if (Array.isArray(data) && data.length > 0) {
          isReadyToUse = true;
        } else if (data && Array.isArray(data.data) && data.data.length > 0) {
          isReadyToUse = true;
        }
      }
      
      // Obter detalhes da resposta
      const responseDetails = {
        status: error ? error.status : 200,
        statusText: error ? error.message : 'OK',
        data: data
      };
      
      // Determinar o erro, se houver
      let errorMessage = null;
      if (!connected) {
        errorMessage = error ? `Erro na conexão: ${error.message}` : 'Erro desconhecido na conexão';
      }
      
      // Criar o resultado
      const result = {
        connected,
        mode,
        error: errorMessage,
        requestDetails: {
          url,
          method: 'GET'
        },
        responseDetails,
        isReady: isReadyToUse
      };
      
      // Armazenar no cache
      cacheService.setConnectionStatus({
        connected,
        mode,
        error: errorMessage,
        isReady: isReadyToUse
      }, CONNECTION_CACHE_DURATION);
      
      console.log(`[n8nProxyService] Teste de conexão concluído: ${connected ? 'Conectado' : 'Falha'}, Pronto para uso: ${isReadyToUse}`);
      
      return result;
    } catch (error) {
      console.error('[n8nProxyService] Erro ao testar conexão:', error);
      
      // Criar resultado de erro
      const result = {
        connected: false,
        error: error.message || 'Erro desconhecido ao testar conexão',
        requestDetails: {
          url: this.sharedN8nConfig.apiUrl,
          method: 'GET'
        },
        isReady: false
      };
      
      // Armazenar no cache
      cacheService.setConnectionStatus({
        connected: false,
        error: result.error,
        isReady: false
      }, CONNECTION_CACHE_DURATION);
      
      return result;
    } finally {
      // Marcar que terminamos o teste
      this.isConnectionTestInProgress = false;
    }
  }
  
  /**
   * Agenda um teste de conexão em segundo plano
   * @returns ID da tarefa em segundo plano
   */
  private scheduleBackgroundConnectionTest(): string {
    const taskId = `connection-test-${generateUniqueId()}`;
    
    // Cria uma promise que executa o teste de conexão
    const task = new Promise<void>((resolve) => {
      setTimeout(async () => {
        try {
          console.log(`[n8nProxyService] Executando teste de conexão em segundo plano (${taskId})...`);
          await this.testConnection(true, false);
          console.log(`[n8nProxyService] Teste de conexão em segundo plano concluído (${taskId})`);
        } catch (error) {
          console.error(`[n8nProxyService] Erro no teste de conexão em segundo plano (${taskId}):`, error);
        } finally {
          // Remove a tarefa da lista
          this.backgroundTasks.delete(taskId);
          resolve();
        }
      }, 1000); // Espera 1 segundo antes de iniciar para não bloquear a interface
    });
    
    // Adiciona à lista de tarefas em segundo plano
    this.backgroundTasks.set(taskId, task);
    
    return taskId;
  }
  
  /**
   * Obtém a lista de workflows do n8n
   * @param useCache Se deve usar cache
   * @returns Lista de workflows
   */
  async getWorkflows(useCache: boolean = true): Promise<any[]> {
    console.log('[n8nProxyService] Obtendo lista de workflows');
    
    try {
      // Obter configurações do usuário
      const config = await this.getN8nConfig();
      
      // Se temos um workflow_id, obter apenas esse workflow
      if (config.workflow_id) {
        console.log(`[n8nProxyService] Obtendo workflow específico: ${config.workflow_id}`);
        const workflow = await this.callN8nApi<any>(`workflows/${config.workflow_id}`, {
          useCache
        });
        
        // Retornar como um array com um único item
        return [workflow];
      }
      
      // Caso contrário, obter todos os workflows
      console.log('[n8nProxyService] Obtendo todos os workflows');
      const workflows = await this.callN8nApi<any[]>('workflows', {
        useCache
      });
      
      return workflows;
    } catch (error) {
      console.error('[n8nProxyService] Erro ao obter workflows:', error);
      throw new Error(`Erro ao obter workflows: ${error.message}`);
    }
  }

  /**
   * Obter um workflow específico
   */
  async getWorkflow(id: string) {
    // Use a URL padrão da API n8n: /workflows/{id}
    return this.callN8nApi(`/workflows/${id}`);
  }

  /**
   * Obter credenciais
   */
  async getCredentials() {
    return this.callN8nApi('credentials');
  }

  /**
   * Atualiza o cache em segundo plano, sem bloquear a UI
   */
  private refreshCacheInBackground<T>(
    endpoint: string,
    options: {
      method?: string;
      body?: any;
      queryParams?: Record<string, string>;
      useCache?: boolean;
      cacheTime?: number;
    },
    cacheKey: string,
    cacheTime: number
  ): void {
    // Criamos uma nova requisição sem usar o cache para atualizar os dados
    setTimeout(async () => {
      try {
        console.log(`[n8nProxyService] Atualizando cache em segundo plano para: ${endpoint}`);
        const freshData = await this.callN8nApi<T>(endpoint, {
          ...options,
          useCache: false // Força uma nova requisição
        });
        
        // Atualiza o cache com os novos dados
        cacheService.set(cacheKey, freshData, cacheTime);
        console.log(`[n8nProxyService] Cache atualizado em segundo plano: ${cacheKey}`);
      } catch (error) {
        console.warn(`[n8nProxyService] Falha ao atualizar cache em segundo plano: ${error}`);
        // Se falhar, mantemos o cache antigo
      }
    }, 100); // Pequeno delay para não bloquear a UI
  }

  /**
   * Verifica o status (ativo ou inativo) de um workflow no n8n
   * @param workflowId ID do workflow a ser verificado
   * @returns Objeto com o resultado da verificação
   */
  async getWorkflowStatus(workflowId: string): Promise<{ success: boolean; active: boolean; message?: string }> {
    try {
      // Obter informações do workflow
      const workflow = await this.getWorkflow(workflowId);
      
      if (!workflow) {
        return {
          success: false,
          active: false,
          message: 'Workflow não encontrado'
        };
      }
      
      return {
        success: true,
        active: workflow.active || false
      };
    } catch (error) {
      console.error(`[n8nProxyService] Erro ao verificar status do workflow ${workflowId}:`, error);
      return {
        success: false,
        active: false,
        message: error instanceof Error ? error.message : 'Erro desconhecido ao verificar status'
      };
    }
  }
  
  /**
   * Ativa ou desativa um workflow no n8n
   * @param workflowId ID do workflow
   * @param active Se true, ativa o workflow; se false, desativa
   * @returns Objeto com o resultado da operação
   */
  async toggleWorkflow(workflowId: string, active: boolean): Promise<{ success: boolean; message?: string }> {
    try {
      console.log(`[n8nProxyService] ${active ? 'Ativando' : 'Desativando'} workflow ${workflowId}`);
      
      // Verificar se o usuário está autenticado
      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        return {
          success: false,
          message: 'Usuário não autenticado'
        };
      }

      // Verificar se o workflow pertence ao usuário atual
      const { data: configData } = await supabase
        .from('integration_config')
        .select('config')
        .eq('type', 'n8n')
        .eq('user_id', user.data.user.id)
        .single();
      
      // Se o workflow_id não corresponder ao do usuário, não permitir a operação
      const userWorkflowId = configData?.config?.workflow_id;
      if (!userWorkflowId || userWorkflowId !== workflowId) {
        console.error(`[n8nProxyService] Tentativa de modificar workflow não pertencente ao usuário: ${workflowId}`);
        return {
          success: false,
          message: 'Você não tem permissão para modificar este workflow'
        };
      }
      
      // Atualizar o status do workflow
      const endpoint = `/workflows/${workflowId}`;
      const response = await this.callN8nApi(endpoint, {
        method: 'PATCH',
        body: { active },
        useCache: false,
        workflowId // Passar o ID explicitamente para garantir que estamos usando o correto
      });
      
      // Limpar cache para forçar atualização dos dados
      cacheService.remove(`n8n:workflows:${workflowId}`);
      cacheService.remove('n8n:workflows');
      
      return {
        success: true,
        message: active ? 'Workflow ativado com sucesso' : 'Workflow desativado com sucesso'
      };
    } catch (error) {
      console.error(`[n8nProxyService] Erro ao ${active ? 'ativar' : 'desativar'} workflow ${workflowId}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : `Erro ao ${active ? 'ativar' : 'desativar'} workflow`
      };
    }
  }

  /**
   * Provisiona um novo workflow para o usuário atual
   * @returns Resultado da operação
   */
  async provisionUserWorkflow(): Promise<{ success: boolean; message?: string; workflow_id?: string }> {
    try {
      console.log('[n8nProxyService] Iniciando provisionamento de workflow para o usuário');
      
      // Verificar se o usuário está autenticado
      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        console.error('[n8nProxyService] Usuário não autenticado');
        return {
          success: false,
          message: 'Usuário não autenticado'
        };
      }
      
      // Verificar se o usuário já possui um workflow
      const { data: configData } = await supabase
        .from('integration_config')
        .select('config')
        .eq('type', 'n8n')
        .eq('user_id', user.data.user.id)
        .single();
      
      if (configData?.config?.workflow_id) {
        console.log('[n8nProxyService] Usuário já possui um workflow:', configData.config.workflow_id);
        return {
          success: true,
          message: 'Workflow já provisionado',
          workflow_id: configData.config.workflow_id
        };
      }
      
      // Chamar a Edge Function de provisionamento usando supabase.functions.invoke
      console.log('[n8nProxyService] Chamando função de provisionamento via supabase.functions.invoke');
      
      // Obter o token de autenticação
      const session = await supabase.auth.getSession();
      const authToken = session.data.session?.access_token;
      
      const { data: result, error } = await supabase.functions.invoke('n8n-provisioner', {
        body: { 
          action: "provision",
          userId: user.data.user.id
        },
        headers: authToken ? {
          Authorization: `Bearer ${authToken}`
        } : undefined
      });
      
      if (error) {
        console.error('[n8nProxyService] Erro na resposta do provisionador:', error);
        return {
          success: false,
          message: `Erro ao provisionar workflow: ${error.message}`
        };
      }
      
      if (!result || !result.success) {
        console.error('[n8nProxyService] Falha no provisionamento:', result);
        return {
          success: false,
          message: result?.message || 'Falha ao provisionar workflow'
        };
      }
      
      const workflowId = result.workspace?.workflow_id;
      console.log('[n8nProxyService] Workflow provisionado com sucesso:', workflowId);
      
      // Limpar o cache de configuração para forçar uma nova leitura
      cacheService.remove('n8n:config');
      
      return {
        success: true,
        message: 'Workflow provisionado com sucesso',
        workflow_id: workflowId
      };
    } catch (error) {
      console.error('[n8nProxyService] Erro ao provisionar workflow:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erro desconhecido ao provisionar workflow'
      };
    }
  }
}

export const n8nProxyService = new N8nProxyService(); 